// Payments Domain Types
//
// Tempii charges practices for two things and moves money for neither party
// beyond that:
//
//   1. Shift fees   — the platform's commission, charged to the practice through
//                     the gateway once it approves its first professional onto a
//                     shift. Governed by the practice's pricing plan.
//   2. Engagement   — what the practice pays the professional for one worked
//      payments       shift date. Settled OFF-PLATFORM. Tempii records that the
//                     practice declared it and that the professional confirmed
//                     receiving it; it never holds or transfers this money.
//
// The escrow model (shift agreements, capture, refund, payout splits) was retired
// with the backend's payments.shift_agreements table.

import type { PaginatedResponse } from "./core";

// ---------------------------------------------------------------------------
// Engagement payments (practice -> professional, off-platform)
// ---------------------------------------------------------------------------

/**
 * State of one worked shift date's payment.
 *
 * `declared` is the practice's claim alone. Only `confirmed` means the
 * professional has said they were paid, so earnings totals must never mix them.
 */
export type DeclaredPaymentStatus =
  | "pending"
  | "declared"
  | "confirmed"
  | "disputed"
  | "resolved";

/**
 * One practice-to-professional payment for one worked shift date.
 *
 * The single web mirror of the backend DTO. Both roles' payment lists come from
 * one projection server-side (`ShiftEngagementPaymentQuery`), so they get one type
 * here too — three near-copies is how `hasEvidence` nearly got added to two of them.
 *
 * Not a `BaseEntity`: the API sends `paymentId`, not `id`, and no `createdBy` /
 * `updatedBy` / `updatedAt`.
 *
 * @see Tempii.Application.Payments.ShiftEngagementPaymentResponse
 */
export interface ShiftEngagementPayment {
  paymentId: string;
  shiftApplicationPostId: string;
  shiftId: string;
  practiceId: string;
  practiceName: string;
  professionalId: string;
  shiftDate: string;
  agreedAmount: number;
  agreedCurrency: string;
  status: DeclaredPaymentStatus;
  declaredAmount?: number | null;
  declaredCurrency?: string | null;
  declaredAt?: string | null;
  declaredMethod?: string | null;
  confirmedAt?: string | null;
  /** Proof is attached; fetch it from the payment-scoped evidence endpoint. */
  hasEvidence: boolean;
  createdAt: string;
}

/**
 * Attaches proof of payment to one worked shift date.
 *
 * Practice-scoped because only the payer attaches proof.
 *
 * @see Tempii.Application.Practices.UploadShiftDatePaymentEvidence.UploadShiftDatePaymentEvidenceCommand
 */
export interface UploadPaymentEvidenceRequest {
  practiceId: string;
  paymentId: string;
  /** Must carry the file under the field name `file`. */
  formData: FormData;
}

export type UploadPaymentEvidenceResponse = {
  value: string;
};

/**
 * Reads proof of payment back. Scoped to the payment rather than to the
 * practice's documents: the professional is the person the proof is meant to
 * convince, and they are not a member of the practice.
 *
 * @see Tempii.Application.Payments.DownloadShiftDatePaymentEvidence.DownloadShiftDatePaymentEvidenceCommand
 */
export interface DownloadPaymentEvidenceRequest {
  paymentId: string;
}

export type DownloadPaymentEvidenceResponse = Blob;

// ---------------------------------------------------------------------------
// Tempii shift fees (practice -> Tempii, real gateway charge)
// ---------------------------------------------------------------------------

export type ShiftFeeStatus =
  | "due"
  | "settling"
  | "settled"
  | "failed"
  | "waived"
  | "voided";

/** @see Tempii.Application.Payments.ShiftFeeResponse */
export interface ShiftFeeItem {
  feeId: string;
  shiftId: string;
  shiftTitle: string;
  practiceId: string;
  practiceName: string;
  status: ShiftFeeStatus;
  feeAmount: number;
  feeCurrency: string;
  /** Snapshotted at assessment — deliberately not re-derived from the plan. */
  feeModel: string;
  feeBasis: string;
  feePercentage?: number | null;
  basisAmount: number;
  assessedAt: string;
  settledAt?: string | null;
  attemptCount: number;
  nextAttemptAt?: string | null;
  lastError?: string | null;
  waivedReason?: string | null;
}

export interface GetFeesRequest {
  status?: ShiftFeeStatus | "";
  page?: number;
  pageSize?: number;
}

export type GetFeesResponse = PaginatedResponse<ShiftFeeItem>;

/** @see Tempii.Application.Admin.WaiveShiftFee.WaiveShiftFeeRequest */
export interface WaiveShiftFeeRequest {
  feeId: string;
  reason: string;
}

// ---------------------------------------------------------------------------
// Subscription invoices (practice -> Tempii, one per billed plan period)
// ---------------------------------------------------------------------------

/** Deliberately the same vocabulary as {@link ShiftFeeStatus} — same settlement machinery. */
export type SubscriptionInvoiceStatus = ShiftFeeStatus;

/** @see Tempii.Application.Payments.SubscriptionInvoiceResponse */
export interface SubscriptionInvoiceItem {
  invoiceId: string;
  practiceId: string;
  practiceName: string;
  pricingPlanId: string;
  planTitle: string;
  /** Snapshotted at issue — deliberately not re-derived from the plan. */
  billingCycle: string;
  periodStart: string;
  periodEnd: string;
  /** Snapshotted at issue: re-pricing a plan never changes what was billed. */
  amount: number;
  currency: string;
  status: SubscriptionInvoiceStatus;
  issuedAt: string;
  settledAt?: string | null;
  attemptCount: number;
  nextAttemptAt?: string | null;
  lastError?: string | null;
  waivedReason?: string | null;
}

export interface GetSubscriptionInvoicesRequest {
  status?: SubscriptionInvoiceStatus | "";
  page?: number;
  pageSize?: number;
}

export type GetSubscriptionInvoicesResponse = PaginatedResponse<SubscriptionInvoiceItem>;

/** @see Tempii.Application.Admin.WaiveSubscriptionInvoice.WaiveSubscriptionInvoiceRequest */
export interface WaiveSubscriptionInvoiceRequest {
  invoiceId: string;
  reason: string;
}

// ---------------------------------------------------------------------------
// Practice billing identity (Stripe customer + stored card for Tempii fees)
// ---------------------------------------------------------------------------

/**
 * @see Tempii.Application.Practices.GetPracticeBillingProfile.PracticeBillingProfileResponse
 *
 * `gatewayEnabled` false means Stripe is not configured on this environment;
 * the card form is hidden entirely rather than offered and broken.
 */
export interface PracticeBillingProfile {
  gatewayEnabled: boolean;
  /** Public by design — it can only tokenise cards, never charge them. */
  publishableKey: string;
  hasPaymentMethod: boolean;
  cardBrand?: string | null;
  cardLast4?: string | null;
  cardExpMonth?: number | null;
  cardExpYear?: number | null;
}

/** @see Tempii.Application.Practices.StartBillingSetup.StartBillingSetupResponse */
export interface StartBillingSetupResponse {
  clientSecret: string;
  publishableKey: string;
}

/** @see Tempii.Application.Practices.CompleteBillingSetup.CompleteBillingSetupRequest */
export interface CompleteBillingSetupRequest {
  practiceId: string;
  setupIntentId: string;
}

/** @see Tempii.Application.Practices.CompleteBillingSetup.PracticeBillingSetupResult */
export interface PracticeBillingSetupResult {
  cardBrand: string;
  cardLast4: string;
  cardExpMonth: number;
  cardExpYear: number;
}

// ---------------------------------------------------------------------------
// Disputes
// ---------------------------------------------------------------------------

/**
 * Either side can dispute a payment record: the professional because a declared
 * payment never arrived, the practice because one it made is being denied.
 *
 * @see Tempii.Application.Practices.RaiseDispute.RaiseDisputeRequest
 * @see Tempii.Application.Professionals.RaiseDispute.RaiseProfessionalDisputeRequest
 */
export interface RaiseDisputeRequest {
  paymentId: string;
  reason: string;
}

/** @see Tempii.Application.Practices.GetDisputes.DisputeResponse */
export interface DisputeItem {
  paymentId: string;
  shiftApplicationPostId: string;
  practiceId: string;
  practiceName: string;
  professionalId: string;
  professionalName: string;
  /** The worked date the two parties disagree about. */
  shiftDate: string;
  status: DeclaredPaymentStatus;
  declaredAmount?: number | null;
  declaredCurrency?: string | null;
  declaredAt?: string | null;
  disputedAt?: string | null;
  /**
   * User id of whoever opened it. Both parties can dispute the same row, so
   * comparing this to the signed-in user is the only way to say "you raised this"
   * rather than "you are answering it".
   */
  disputedBy?: string | null;
  disputeReason?: string | null;
  disputeResolvedAt?: string | null;
  disputeResolution?: string | null;
  createdAt: string;
}

export interface GetDisputesRequest {
  page: number;
  pageSize: number;
}

/**
 * The disputes GET endpoints return the standard paginated envelope. This type
 * previously claimed flat pagination fields that never existed on the wire —
 * unnoticed because no component consumed it.
 */
export type GetDisputesResponse = PaginatedResponse<DisputeItem>;

/**
 * An admin's finding on a disputed payment.
 *
 * Carries no payout split: there is no Tempii-held balance to divide, so
 * resolving records a conclusion and closes the thread.
 *
 * @see Tempii.Application.Admin.ResolveDispute.ResolveDisputeRequest
 */
export interface ResolveDisputeRequest {
  resolution: string;
}

// ---------------------------------------------------------------------------
// Shared filter/sort options
// ---------------------------------------------------------------------------

export type PaymentStatusFilter = "" | DeclaredPaymentStatus;

export const PAYMENT_STATUS_FILTER_OPTIONS: {
  value: PaymentStatusFilter;
  label: string;
}[] = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Awaiting payment" },
  { value: "declared", label: "Marked paid" },
  { value: "confirmed", label: "Confirmed received" },
  { value: "disputed", label: "Disputed" },
  { value: "resolved", label: "Dispute resolved" }
];

/** Status options without the empty "all" row (for FilterSelect placeholders). */
export const PAYMENT_STATUS_VALUE_OPTIONS =
  PAYMENT_STATUS_FILTER_OPTIONS.filter(
    (option): option is { value: DeclaredPaymentStatus; label: string } =>
      option.value !== ""
  );

/**
 * Must stay in step with `ShiftEngagementPaymentQuery.SortConfiguration()`.
 *
 * A key offered here that the server does not know makes `ApplySorting` throw
 * `ArgumentException`, which surfaces as a 500 — not a validation error. That is
 * what `DeclaredAmount` did before the server learned to sort by it.
 */
export const PAYMENT_SORT_OPTIONS = [
  { value: "CreatedAt", label: "Date Created" },
  { value: "ShiftDate", label: "Shift Date" },
  { value: "DeclaredAmount", label: "Amount" },
  { value: "Status", label: "Status" }
] as const;

/**
 * Tenant-wide payment counters, for the admin and platform consoles.
 *
 * Fees are what practices owe Tempii; engagement payments are what practices pay
 * professionals off-platform. The two are never summed — see `payments.ts` header.
 *
 * @see Tempii.Application.Payments.GetPaymentStats.GetPaymentStatsResponse
 */
export interface PaymentStatsResponse {
  totalFees: number;
  feesDue: number;
  feesSettled: number;
  feesFailed: number;
  totalFeesDueAmount: number;
  totalFeesSettledAmount: number;
  totalEngagementPayments: number;
  /** Declared and not yet confirmed — the practice's claim alone. */
  declaredEngagementPayments: number;
  confirmedEngagementPayments: number;
  /** Disputed plus resolved — every payment a disagreement ever touched. */
  disputedEngagementPayments: number;
  /** Still awaiting an admin finding. */
  openDisputes: number;
  /** Closed with a finding recorded. */
  resolvedDisputes: number;
  /** Declared value of everything argued about — not money Tempii holds. */
  totalDisputedAmount: number;
  totalConfirmedEngagementAmount: number;
  // Subscription invoices — the same kind of money as the fees above, counted
  // separately so the transaction-fees / premium-services split stays honest.
  totalInvoices: number;
  invoicesDue: number;
  invoicesSettled: number;
  invoicesFailed: number;
  totalInvoicesDueAmount: number;
  totalInvoicesSettledAmount: number;
}
